import { type Answer, ConsentStamp, type FieldId, Sha256 } from '@eformulieren/model'
import type * as Y from 'yjs'

/**
 * Het akkoord per persoon in het sectiedocument (besluit van de product owner van 3 november 2026,
 * eisenboek 12.56).
 *
 * **Een eigen wortel per veld, en alleen de pod schrijft erin.** Een `Y.Map` met de naam van het veld, met
 * per persoon een stempel onder `akkoord:<persoon>` en de hash van de inhoud van nu onder
 * {@link STAND_KEY}. De pod zet de stempel uit het token (persoon en e-mailadres), zijn eigen klok en de
 * hash van de inhoud (`apps/runtime/src/consents.ts`), en houdt de stand bij na elke wijziging van de
 * inhoud; een wijziging van een browser in deze map zet hij terug (`consentGuard.ts`). Wat de browser
 * stuurt is alleen "ik geef akkoord" of "ik trek in", langs HTTP.
 *
 * **Per persoon een sleutel, en geen lijst in één waarde.** Twee mensen die tegelijk akkoord geven, raken
 * elk hun eigen sleutel; een lijst zou de een de ander laten overschrijven. Elke `set` blijft staan zolang
 * de opruiming uit staat (valkuil 1); een akkoord is één klik per persoon, en de stand zet de pod alleen
 * als hij anders is, en dan hoogstens eens per seconde.
 *
 * **Na een crash van de pod** kan een browser die opnieuw verbindt een stempel meesturen die de pod niet
 * meer heeft. Die zet de pod terug, want hij komt van een browser; die persoon geeft dan opnieuw akkoord.
 * Dicht is de veilige kant: een stempel die de pod niet zelf zette, telt niet.
 */

/** De sleutel van de stand van nu in de map van een akkoord per persoon. */
export const STAND_KEY = '$stand'
/** Het voorvoegsel van een stempel per persoon. */
export const CONSENT_PREFIX = 'akkoord:'

/** De map van dit akkoord per persoon; een wortel met de naam van het veld. */
export function consentMap(doc: Y.Doc, fieldId: FieldId | string): Y.Map<unknown> {
  return doc.getMap<unknown>(fieldId)
}

/** Stempels en stand zoals ze in het document staan; wat niet op het model past, telt niet. */
export function consentsIn(map: Y.Map<unknown>): { readonly akkoorden: readonly ConsentStamp[]; readonly stand: string | undefined } {
  const akkoorden: ConsentStamp[] = []
  for (const [key, raw] of map) {
    if (!key.startsWith(CONSENT_PREFIX)) continue
    const stamp = ConsentStamp.safeParse({ ...(typeof raw === 'object' && raw !== null ? raw : {}), persoon: key.slice(CONSENT_PREFIX.length) })
    if (stamp.success) akkoorden.push(stamp.data)
  }
  akkoorden.sort((a, b) => (a.gegevenOp < b.gegevenOp ? -1 : a.gegevenOp > b.gegevenOp ? 1 : a.persoon < b.persoon ? -1 : 1))
  const stand = Sha256.safeParse(map.get(STAND_KEY))
  return { akkoorden, stand: stand.success ? stand.data : undefined }
}

/** Zet de stand, alleen als hij anders is: elke `set` blijft in het document (valkuil 1). Levert of er iets veranderde. */
export function setStand(map: Y.Map<unknown>, stand: string): boolean {
  if (map.get(STAND_KEY) === stand) return false
  map.set(STAND_KEY, stand)
  return true
}

/** Zet de stempel van deze persoon. */
export function setConsent(map: Y.Map<unknown>, stamp: ConsentStamp): void {
  map.set(`${CONSENT_PREFIX}${stamp.persoon}`, {
    email: stamp.email,
    gegevenOp: stamp.gegevenOp,
    stand: stamp.stand,
    ...(stamp.namens === undefined ? {} : { namens: stamp.namens }),
  })
}

/** Haalt de stempel van deze persoon weg; levert of er een was. */
export function removeConsent(map: Y.Map<unknown>, person: string): boolean {
  const key = `${CONSENT_PREFIX}${person}`
  if (!map.has(key)) return false
  map.delete(key)
  return true
}

/**
 * Het antwoord van een akkoord per persoon in de stand (`answerOf`): `waarde` zegt of er minstens één
 * akkoord is dat nog geldt, en `akkoorden` en `stand` staan ernaast, zodat de regel (`consentOverview` in
 * de engine) en de revisie per persoon weten wat er gebeurde. De herkomst is de laatste stempel: wie het
 * laatst akkoord gaf, en wanneer. Zonder stempel is er geen antwoord, want een antwoord zonder herkomst
 * weigert het schema van een revisie.
 */
export function consentAnswer(doc: Y.Doc, fieldId: FieldId | string): Answer | undefined {
  const { akkoorden, stand } = consentsIn(consentMap(doc, fieldId))
  const latest = akkoorden.at(-1)
  if (latest === undefined) return undefined
  const valid = stand !== undefined && akkoorden.some((stamp) => stamp.stand === stand)
  return { waarde: valid, door: latest.persoon, gewijzigdOp: latest.gegevenOp, akkoorden: [...akkoorden], ...(stand === undefined ? {} : { stand }) }
}

/** Zet een antwoord uit een revisie terug in het document, zoals de pod het zaait (`seedDocument`). */
export function seedConsents(doc: Y.Doc, fieldId: FieldId | string, answer: Answer): void {
  if (!('waarde' in answer)) return
  const map = consentMap(doc, fieldId)
  doc.transact(() => {
    if (answer.stand !== undefined) setStand(map, answer.stand)
    for (const stamp of answer.akkoorden ?? []) setConsent(map, stamp)
  })
}
